import classNames from "classnames";
import { useMemo, useState } from "react";
import { useCategoryLabelGetter } from "../../../hooks/useCategoryLabelGetter";
import { useEntities } from "../../../hooks/useEntities";
import { useFinancialAccounts } from "../../../hooks/useFinancialAccounts";
import {
    useWorkspaceRuleGroups,
    useDefaultAccountingRules,
} from "../../../api/rules.api";
import { FiltersTypes } from "../../../common/types/filters/general";
import { useWorkspaceAccounts } from "../../../api/account.api";
import { useClasses } from "../../../api/class.api";
import { RulesPageHeader } from "./RulesPageHeader";
import styles from "./RulesPage.module.scss";
import { buildRulesItems } from "./utils";
import { RulesTable } from "./RulesTable/RulesTable";

export const RulesPage: React.FC = () => {
    const categoryLabelGetter = useCategoryLabelGetter();
    const entities = useEntities();
    const financialAccounts = useFinancialAccounts();
    const { classesMap } = useClasses();
    const accounts = useWorkspaceAccounts();

    const [filters, setFilters] = useState<
        Pick<FiltersTypes, "entityIds" | "rulesEnabledOnly">
    >({
        entityIds: entities.map((entity) => entity.id),
        rulesEnabledOnly: true,
    });

    const ruleGroups = useWorkspaceRuleGroups();

    const defaultAccountingRules = useDefaultAccountingRules();

    const [searchQuery, setSearchQuery] = useState("");

    const filteredRuleGroups = useMemo(
        () =>
            buildRulesItems({
                search: searchQuery,
                categoryLabelGetter,
                ruleGroups: ruleGroups?.data?.ruleGroups,
                entities,
                financialAccounts,
                counterparties: ruleGroups?.data?.counterparties ?? [],
                filters,
                accounts: accounts.data ?? [],
                classesMap,
            }),
        [
            searchQuery,
            categoryLabelGetter,
            ruleGroups,
            entities,
            financialAccounts,
            filters,
            accounts,
            classesMap,
        ],
    );

    const accountingRuleGroups = useMemo(
        () =>
            buildRulesItems({
                search: searchQuery,
                categoryLabelGetter,
                ruleGroups: defaultAccountingRules?.data?.ruleGroup
                    ? [defaultAccountingRules?.data?.ruleGroup]
                    : [],
                entities,
                financialAccounts,
                counterparties: ruleGroups?.data?.counterparties ?? [],
                filters,
                accounts: accounts.data ?? [],
                classesMap,
            }),
        [
            searchQuery,
            categoryLabelGetter,
            defaultAccountingRules?.data?.ruleGroup,
            entities,
            financialAccounts,
            ruleGroups?.data?.counterparties,
            filters,
            accounts,
            classesMap,
        ],
    );

    return (
        <div className={classNames(styles.rules, "settings__content")}>
            <RulesPageHeader
                setSearch={setSearchQuery}
                search={searchQuery}
                filters={filters}
                onFiltersChange={setFilters}
            />
            <div className="my-4"></div>
            <RulesTable
                groups={[...(filteredRuleGroups ?? [])]}
                accountingGroup={accountingRuleGroups?.[0]}
                counterparties={ruleGroups?.data?.counterparties ?? []}
                transactionCounts={ruleGroups?.data?.transactionCounts ?? {}}
            />
        </div>
    );
};
